import { useCallback, useEffect, useRef } from 'react';
import { saveLayoutToDatabase } from '../../services/layout.service';
import { applyCollapsedStateToLayout } from '../../utils/categoryCollapsedState';

/**
 * Persist per-category collapsed (minimize/maximize) state into the existing
 * Layout document via POST only. Debounced to avoid spamming the server.
 *
 * Important: we do **not** dispatch the patched layout back into Redux here.
 * Doing so replaced `hospitalLayout` (etc.) with new object references on
 * every toggle ~400ms later, which re-rendered the whole dashboard mid-
 * Collapse / RGL animation and made inner cards flicker or vanish briefly.
 * Open/close UI stays driven by page `collapsedCategoryIds`; the DB still gets
 * `categories[].collapsed` for reload. Full layout save / explicit
 * `handleSaveLayout` still runs `applyCollapsedStateToLayout` so Redux and DB
 * stay aligned when the user saves the grid.
 *
 * @param {Object} params
 * @param {string} params.page - PAGE_TYPE value for the current page
 * @param {string} params.layoutType - 'personal' | 'default'
 * @param {Object} params.layout - canonical current layout from Redux (read-only snapshot for save body)
 * @param {string[]} params.cards - currently selected cards for this page
 * @param {number} [params.debounceMs=400] - debounce window for burst toggles
 * @returns {(collapsedMap: Record<string, boolean>) => void}
 */
export default function useCollapsedCategoriesPersistence({
	page,
	layoutType,
	layout,
	cards,
	debounceMs = 400,
}) {
	const latestRef = useRef({ page, layoutType, layout, cards });
	latestRef.current = { page, layoutType, layout, cards };

	const pendingMapRef = useRef(null);
	const timerRef = useRef(null);

	const flush = useCallback(async () => {
		const collapsedMap = pendingMapRef.current;
		if (!collapsedMap) return;
		pendingMapRef.current = null;
		const {
			page: currentPage,
			layoutType: currentLayoutType,
			layout: currentLayout,
			cards: currentCards,
		} = latestRef.current;
		if (!currentLayout || typeof currentLayout !== 'object') return;
		try {
			const withCollapsed = applyCollapsedStateToLayout(currentLayout, collapsedMap);
			await saveLayoutToDatabase(
				{
					page: currentPage,
					layout: withCollapsed,
					cards: Array.isArray(currentCards) ? currentCards : [],
					timestamp: new Date().toISOString(),
				},
				currentLayoutType || 'personal'
			);
		} catch (err) {
			console.error('Failed to persist collapsed category state:', err);
		}
	}, []);

	const persistCollapsedState = useCallback(
		(collapsedMap) => {
			pendingMapRef.current = collapsedMap;
			if (timerRef.current) clearTimeout(timerRef.current);
			timerRef.current = setTimeout(() => {
				timerRef.current = null;
				flush();
			}, debounceMs);
		},
		[flush, debounceMs]
	);

	useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
				flush();
			}
		};
	}, [flush]);

	return persistCollapsedState;
}
